// Event start dates are split across two fields:
//   - start_date: the calendar day, anchored to noon UTC so it never shifts day
//     across time zones (see toNoonUTC in services/eventService.js and
//     normalizeToNoonUTC in components/minimal/hook-form/RHFDatePicker.jsx).
//   - start_hour: a full datetime whose only meaningful part is the time of day.
//
// The app renders event days in Asia/Jerusalem (see fDate in
// utils/minimal/formatTime.js). These helpers keep the day owned by start_date
// and the time owned by start_hour, so the calendar, dashboard and seller page
// can never disagree about which day an event falls on.

const JERUSALEM_TZ = 'Asia/Jerusalem';

// Calendar parts (year, 0-based month, day) of an instant as seen in Jerusalem.
const jerusalemYMD = (date) => {
    // sv-SE formats as ISO-like "YYYY-MM-DD", easy to split.
    const [y, m, d] = new Date(date)
        .toLocaleDateString('sv-SE', { timeZone: JERUSALEM_TZ })
        .split('-')
        .map(Number);
    return { y, m: m - 1, d };
};

// Anchor a date picked from a calendar to noon UTC, using its *local* calendar
// parts. A bare MUI DatePicker yields local midnight (e.g. picking the 16th in
// Israel gives 21:00Z on the 15th); reading local parts recovers the intended
// day, and noon UTC then survives any time-zone conversion without shifting.
export const normalizePickedDateToNoonUTC = (date) => {
    if (!date) return date;
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0));
};

// Build a moment whose day comes from `daySource` (start_date) and whose time of
// day comes from `timeSource` (start_hour). Returned in local time so it renders
// on the right Jerusalem day in FullCalendar and matches the list's fDate output,
// even when start_hour's stored date has drifted to a different day.
export const combineDayAndTime = (daySource, timeSource) => {
    if (!daySource || !timeSource) return timeSource || daySource;
    const day = new Date(daySource);
    const time = new Date(timeSource);
    if (isNaN(day.getTime()) || isNaN(time.getTime())) return timeSource;
    const { y, m, d } = jerusalemYMD(day);
    return new Date(y, m, d, time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
};
